import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Group, Title, Text, Button, Skeleton, Card, ThemeIcon, ActionIcon, Box } from '@mantine/core';
import { IconArrowRight, IconBooks, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import type { CourseModelView } from '@actschurch/ccms-sdk';
import { CourseCard } from '@/components/CourseCard';

/**
 * Each card occupies a fixed slice of the rail — wide enough to read, narrow
 * enough that the next card peeks in, signalling there's more to scroll.
 */
const CARD_WIDTH = 'clamp(236px, 80vw, 300px)';

/** Page-scroll animation duration, ms. Short enough to feel instant-ish. */
const SCROLL_DURATION = 320;

/**
 * Horizontally scroll an element by `delta` px over a fixed, snappy duration.
 * Sets `scrollLeft` directly via requestAnimationFrame so it's independent of
 * the browser's (often slow) native smooth-scroll and of perfect-scrollbar's
 * overflow:hidden container. Clamps to the scrollable range and honours
 * prefers-reduced-motion by jumping instantly.
 */
function smoothScrollLeftBy(el: HTMLElement, delta: number) {
  const max = el.scrollWidth - el.clientWidth;
  const start = el.scrollLeft;
  const target = Math.max(0, Math.min(max, start + delta));
  const distance = target - start;
  if (Math.abs(distance) < 1) return;

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    el.scrollLeft = target;
    return;
  }

  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
  let startTime: number | null = null;
  const step = (now: number) => {
    startTime ??= now;
    const t = Math.min(1, (now - startTime) / SCROLL_DURATION);
    el.scrollLeft = start + distance * easeOutCubic(t);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export interface CourseSectionProps {
  title: string;
  description?: string;
  /** Small icon shown in a tinted square next to the title. */
  icon?: ReactNode;
  /** When set, a "see all" link button is shown on the right of the header. */
  viewAllHref?: string;
  viewAllLabel?: string;
  courses: CourseModelView[];
  isLoading?: boolean;
  emptyText?: string;
  skeletonCount?: number;
}

/**
 * A titled, horizontally-scrolling rail of course cards — the home page's
 * repeated building block (Featured, Latest, …).
 *
 * The rail uses `react-perfect-scrollbar` for a consistent, lightweight
 * scrollbar across browsers. It scrolls by touch/trackpad swipe everywhere;
 * on pointer devices a pair of chevron buttons page through it. Cards keep a
 * fixed width so the next one always peeks in, making the rail's
 * scrollability obvious.
 */
export function CourseSection({
  title,
  description,
  icon,
  viewAllHref,
  viewAllLabel = 'See all',
  courses,
  isLoading = false,
  emptyText = 'Nothing here yet — check back soon.',
  skeletonCount = 3,
}: CourseSectionProps) {
  // The native scroll container behind perfect-scrollbar — captured so the
  // chevron buttons can drive it and we can read its scroll position.
  const railRef = useRef<HTMLElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  // Starts true so the chevrons stay hidden until we've confirmed the rail
  // actually overflows its viewport.
  const [atEnd, setAtEnd] = useState(true);

  const syncEdges = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= max - 1);
  }, []);

  // Recompute the edge state whenever the content or the viewport size
  // changes — drives both the chevron visibility and their disabled state.
  useEffect(() => {
    syncEdges();
    window.addEventListener('resize', syncEdges);
    return () => window.removeEventListener('resize', syncEdges);
  }, [syncEdges, isLoading, courses.length]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = railRef.current;
    if (!el) return;
    // Drive the scroll with a short fixed-duration tween rather than the native
    // `behavior: 'smooth'`. Against perfect-scrollbar's overflow:hidden
    // container the native smooth scroll animates sluggishly and its speed is
    // browser-controlled; a rAF tween setting scrollLeft directly is snappy and
    // consistent (and perfect-scrollbar still tracks it via the scroll event).
    smoothScrollLeftBy(el, direction * Math.round(el.clientWidth * 0.85));
  };

  const showChevrons = !isLoading && courses.length > 0 && !(atStart && atEnd);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap" gap="xs">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {icon && (
            <ThemeIcon size={38} radius="md" variant="light" color="brand" visibleFrom="xs">
              {icon}
            </ThemeIcon>
          )}
          <div style={{ minWidth: 0 }}>
            <Title order={2} style={{ fontSize: 'clamp(1.25rem, 2vw + 0.7rem, 1.6rem)' }}>
              {title}
            </Title>
            {description && (
              <Text c="dimmed" size="sm">
                {description}
              </Text>
            )}
          </div>
        </Group>

        <Group gap="xs" wrap="nowrap">
          {showChevrons && (
            <Group gap={4} wrap="nowrap" visibleFrom="sm">
              <ActionIcon
                variant="default"
                radius="xl"
                size="lg"
                onClick={() => scrollByPage(-1)}
                disabled={atStart}
                aria-label={`Scroll ${title} backward`}
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
              <ActionIcon
                variant="default"
                radius="xl"
                size="lg"
                onClick={() => scrollByPage(1)}
                disabled={atEnd}
                aria-label={`Scroll ${title} forward`}
              >
                <IconChevronRight size={18} />
              </ActionIcon>
            </Group>
          )}
          {viewAllHref && (
            <Button
              component={Link}
              to={viewAllHref}
              variant="subtle"
              size="sm"
              rightSection={<IconArrowRight size={16} />}
            >
              {viewAllLabel}
            </Button>
          )}
        </Group>
      </Group>

      {isLoading ? (
        <div style={{ overflow: 'hidden' }}>
          <Group gap="lg" wrap="nowrap" align="stretch" py={6} style={{ width: 'max-content' }}>
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <Box key={i} style={{ flex: '0 0 auto', width: CARD_WIDTH }}>
                <Skeleton height={300} radius="lg" />
              </Box>
            ))}
          </Group>
        </div>
      ) : courses.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconBooks size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">{emptyText}</Text>
          </Stack>
        </Card>
      ) : (
        <PerfectScrollbar
          containerRef={(el) => {
            railRef.current = el;
          }}
          onScrollX={syncEdges}
          options={{ suppressScrollY: true, wheelPropagation: true }}
          style={{ width: '100%' }}
        >
          <Group gap="lg" wrap="nowrap" align="stretch" py={6} style={{ width: 'max-content' }}>
            {courses.map((course) => (
              <Box key={course.courseID} style={{ flex: '0 0 auto', width: CARD_WIDTH }}>
                <CourseCard
                  course={course}
                  categoryName={course.categoryName}
                  categoryColor={course.categoryColor}
                  ministryName={course.ministryName}
                />
              </Box>
            ))}
          </Group>
        </PerfectScrollbar>
      )}
    </Stack>
  );
}
